/**
 * table visibility observer checks if the htmlElement in the table is visible or hidden by the header
 * also, it hides or unhides tippy tooltips on the node, but the tippy instance must be on the same element
 * for reference see {@see InputFieldTableCell.svelte}
 */
export function observeTableVisibility(node: HTMLElement, enableLogging: boolean = false) {
	// Core state variables - only initialize what's needed
	let observer: IntersectionObserver
	let tippy: any
	let isFocused = false
	let scrollContainer: Element | Window | null = null
	let isTooltipHiddenByUs = false // Track if we manually hid the tooltip

	const nodeId = enableLogging ? node.id || `node-${Math.random().toString(16).slice(2)}` : ''

	// Logger - only create if logging enabled
	const log = enableLogging
		? // oxlint-disable-next-line no-console This is a logging function which is only active when logging is enabled
			(message: string, data?: unknown) => console.log(`[${nodeId}] ${message}`, data || '')
		: () => {}

	// Throttled function for scroll events - 60fps performance
	const throttle = (func: (target: EventTarget) => void, delay: number) => {
		let timeoutId: number
		let lastExecTime = 0
		return (...args: any[]) => {
			const currentTime = Date.now()
			if (currentTime - lastExecTime > delay) {
				func(...args)
				lastExecTime = currentTime
			} else {
				clearTimeout(timeoutId)
				timeoutId = setTimeout(
					() => {
						func(...args)
						lastExecTime = Date.now()
					},
					delay - (currentTime - lastExecTime)
				)
			}
		}
	}

	// Check if input is completely hidden behind header (bottom of input <= bottom of header)
	const isCompletelyHidden = (inputRect: DOMRect, headerRect: DOMRect): boolean =>
		inputRect.bottom <= headerRect.bottom

	// Main visibility check - handles both hide/show logic
	const checkVisibility = () => {
		if (!isFocused || !tippy) return

		const stickyHeader = node.closest('table')?.querySelector('thead tr:first-child') as HTMLElement
		if (!stickyHeader) return

		const inputRect = node.getBoundingClientRect()
		const headerRect = stickyHeader.getBoundingClientRect()
		const isHidden = isCompletelyHidden(inputRect, headerRect)

		log('Visibility check:', { isHidden, isTooltipHiddenByUs })

		if (isHidden && !isTooltipHiddenByUs) {
			log('🚫 Hiding tooltip - input completely behind header')
			tippy.hide()
			isTooltipHiddenByUs = true
		} else if (!isHidden && isTooltipHiddenByUs) {
			log('👁️ Showing tooltip - input visible again')
			tippy.show()
			isTooltipHiddenByUs = false
		}
	}

	// Throttled scroll handler
	const throttledVisibilityCheck = throttle(checkVisibility, 16)

	// Create intersection observer - lazy initialization
	const createObserver = () => {
		const table = node.closest('table')
		const stickyHeader = table?.querySelector('thead tr:first-child') as HTMLElement
		if (!stickyHeader) return null

		return new IntersectionObserver((entries) => entries.forEach(() => checkVisibility()), {
			root: table,
			rootMargin: `${-stickyHeader.offsetHeight}px 0px 0px 0px`,
			threshold: [0, 0.1, 0.5, 1]
		})
	}

	// Find scrollable container - cached for performance
	const findScrollContainer = () => {
		let parent = node.closest('table')?.parentElement
		while (parent && parent !== document.body) {
			const { overflowY, overflow } = getComputedStyle(parent)
			if (['scroll', 'auto'].includes(overflowY) || ['scroll', 'auto'].includes(overflow)) {
				return parent
			}
			parent = parent.parentElement
		}
		return window
	}

	// Event handlers
	const handleFocus = () => {
		isFocused = true
		isTooltipHiddenByUs = false // Reset state on focus
		log('🎯 Focus - starting observation')
		startObserving()
	}

	const handleBlur = () => {
		isFocused = false
		isTooltipHiddenByUs = false // Reset state on blur
		log('😴 Blur - stopping observation')
		stopObserving()
	}

	const handleScroll = () => throttledVisibilityCheck()

	// Start/stop observation - only initialize when needed
	const startObserving = () => {
		if (!isFocused || !tippy) return

		// Start intersection observer
		if (!observer) {
			observer = createObserver()
			if (observer) {
				observer.observe(node)
				log('✅ IntersectionObserver started')
			}
		}

		// Add scroll listener
		if (!scrollContainer) {
			scrollContainer = findScrollContainer()
			scrollContainer.addEventListener('scroll', handleScroll, { passive: true })
			log('✅ Scroll listener added')
		}

		// Initial visibility check
		setTimeout(checkVisibility, 10)
	}

	const stopObserving = () => {
		// Stop intersection observer
		if (observer) {
			observer.disconnect()
			observer = null
			log('✅ IntersectionObserver stopped')
		}

		// Remove scroll listener
		if (scrollContainer) {
			scrollContainer.removeEventListener('scroll', handleScroll)
			scrollContainer = null
			log('✅ Scroll listener removed')
		}
	}

	// Initialize tippy reference - retry until available
	const initTippy = () => {
		tippy = node._tippy
		if (!tippy) {
			setTimeout(initTippy, 50)
		} else {
			log('🔍 Tippy initialized')
		}
	}

	// Setup event listeners
	node.addEventListener('focusin', handleFocus, true)
	node.addEventListener('focusout', handleBlur, true)

	// Initialize tippy
	setTimeout(initTippy, 0)
	log('🚀 Observer initialized')

	// Cleanup function
	return {
		destroy() {
			log('🗑️ Destroying observer')
			stopObserving()
			node.removeEventListener('focusin', handleFocus, true)
			node.removeEventListener('focusout', handleBlur, true)
		}
	}
}
